<template>
	<div class="impact-form">
		<div class="impact-form__content">
			<div class="impact-form__header">
				<!-- DEV: hardcoded title for testing, This title should be dynamic depending on the users selection from the menu button. The title options are:
			New Impact, Edit Impact, and Copy Impact -->
				<div class="impact-form__header-primary">
					<span class="impact-form__header-title">
						{{ titleData }}
					</span>

					<!-- DEV: the autosave toggle should be enabled by default and will save the document every 5 minutes.  -->
					<span class="impact-form__header-autosave">
						<Toggle
							:label="'Autosave'"
							:disabled="!editionMode"
							:value="autoSave"
							@change="changeAutoSave"
							ref="tooltipAutosaveRef"
							@mouseenter.native="tooltipAutosaveVisible = true"
							@focus.native="tooltipAutosaveVisible = true"
							@mouseleave.native="tooltipAutosaveVisible = false"
							@blur.native="tooltipAutosaveVisible = false"
						>
							<template slot="true-value">on</template>
							<template slot="false-value">off</template>
						</Toggle>
						<Tooltip
							class="impact-form__header-secondary-tooltip"
							:variation="variation"
							:text="editionMode ? status : 'Not allowed'"
							:trackedRefName="'tooltipAutosaveRef'"
							:display="tooltipAutosaveVisible"
							:offset="[4, 4]"
							position="top-start"
						/>
					</span>
				</div>
				<span class="impact-form__header-spacer" />
				<div class="impact-form__header-secondary">
					<Toggle
						class="impact-form__header-secondary-toggle"
						:label="
							editionMode
								? form.active
									? 'Active'
									: 'Inactive'
								: 'Active'
						"
						:disabled="!editionMode"
						:value="form.active"
						@change="changeActiveDeactivate"
					/>
					<span class="impact-form__header-spacer" />
					<Button
						:className="'btn--icon-only-circle'"
						labelText=""
						:btnClick="showFormsHistory"
						iconClassName="icon-history"
						:iconBeforeLabel="true"
						:disabled="!editionMode"
					/>
				</div>
			</div>
			<hr class="impact-form__header-horizontal-line" />
			<div class="impact-form__group">
				<div class="impact-form__group-primary">
					<div class="impact-form__group-item">
						<Input
							className="field__input"
							label="Form Name"
							:maxLength="20"
							:required="true"
							v-model="form.name"
							:forceCustomErrorMessage="
								formNameCustomErrorMessage
							"
							@input="setName"
						/>
					</div>

					<div class="impact-form__group-item">
						<Input
							className="field__input"
							label="Form Revision"
							v-model="form.formRevision"
							:maxLength="100"
							:forceCustomErrorMessage="
								formRevisionCustomErrorMessage
							"
							@input="setFormRevision"
						/>
					</div>
				</div>

				<div class="impact-form__group-secondary">
					<div class="impact-form-group__item">
						<Textarea
							label="Form Description"
							:className="'textarea__input'"
							:cols="30"
							:maxLength="100"
							:required="true"
							:rows="5"
							:showAlwaysCharacterCounter="true"
							v-model="form.description"
							:forceCustomErrorMessage="
								formDescriptionCustomErrorMessage
							"
							@input="setDescription"
						/>
					</div>
				</div>
			</div>

			<div class="impact-form__main-content">
				<ImpactFormContent v-model="form" :editionMode="editionMode" />
			</div>
		</div>
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import Toggle from '@/components/forms/selection-control/toggle/toggle';
import Input from '@/components/forms/input/input';
import Textarea from '@/components/forms/textarea/textarea.vue';
import ImpactFormContent from '@/views/form-designer/impact-form-content.vue';
import CookiesConfig from '@/config/cookies';
import LaserImpactForm from '@/mixins/forms/laser-impact-form';
import Tooltip from '@/components/shells/tooltip/tooltip';
import FormShared from '@/mixins/forms/form-shared';
import { required, maxLength } from 'vuelidate/lib/validators';

export default {
	name: 'ImpactForm',
	mixins: [LaserImpactForm, FormShared],
	model: {
		prop: 'form'
	},
	components: {
		Button,
		Toggle,
		Input,
		Textarea,
		ImpactFormContent,
		Tooltip
	},
	props: {
		form: {
			type: Object,
			default: () => {}
		}
	},
	validations: {
		form: {
			name: {
				required,
				maxLength: maxLength(20)
			},
			formRevision: {
				maxLength: maxLength(100)
			},
			description: {
				required
			}
		}
	},
	data() {
		return {
			autoSave: false,
			tooltipAutosaveVisible: false
		};
	},
	mounted() {
		if (this.editionMode) {
			const { name } = CookiesConfig.globalFormAutoSave;
			this.autoSave = this.$cookies.get(name) === 'true';
		}
	},
	computed: {
		formNameCustomErrorMessage() {
			if (this.$v.form.name.$dirty) {
				if (!this.$v.form.name.required) {
					return 'Form Name is required';
				} else if (!this.$v.form.name.maxLength) {
					return `Form Name must have less than ${this.$v.form.name.$params.maxLength.max} letters`;
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		formRevisionCustomErrorMessage() {
			if (this.$v.form.formRevision.$dirty) {
				if (!this.$v.form.formRevision.maxLength) {
					return `Form Revision must have less than ${this.$v.form.formRevision.$params.maxLength.max} letters`;
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		formDescriptionCustomErrorMessage() {
			if (this.$v.form.description.$dirty) {
				if (!this.$v.form.description.required) {
					return 'Form Description is required';
				} else {
					return '';
				}
			} else {
				return '';
			}
		}
	},
	methods: {
		changeAutoSave(value) {
			this.autoSave = value;
			const { name, expiredTime } = CookiesConfig.globalFormAutoSave;
			this.$cookies.set(name, value, expiredTime);
		},
		setName() {
			const hasError =
				!this.$v.form.name.required || !this.$v.form.name.maxLength;
			this.$store.commit('form/SetHasError', hasError);
		},
		setFormRevision() {
			this.$store.commit(
				'form/SetHasError',
				!this.$v.form.formRevision.maxLength
			);
		},
		setDescription() {
			const hasError = !this.$v.form.description.required;
			this.$store.commit('form/SetHasError', hasError);
		}
	}
};
</script>

<style lang="scss" scoped>
/**
  * [Description]
  *
  * Impact Forms designer
  * Can use SCSS variables = TRUE
  *
  *
**/

.impact-form {
	&__content {
		background: $white;
		padding: $padding-md;
		flex-direction: column;
		flex-wrap: wrap;
		display: flex;

		@include mq($until: sm-l) {
			border-top: 1px solid $neutral-3;
		}

		@include mq($from: sm-l) {
			border: 1px solid $neutral-3;
			border-radius: 4px;
		}
	}

	&__header-horizontal-line {
		@include create-horizontal-line(16px, 1px, $gutter-xl, $neutral-3);
	}

	&__header {
		@include mq($from: md) {
			display: flex;
			align-items: center;
		}

		&-title {
			color: $black;
			width: calc(100% - 180px);
			@include ellipsis;
			font-size: $default-font-size;
			font-weight: $medium-font-weight;

			@include mq($from: sm-l) {
				display: flex;
				align-items: center;
			}
		}

		&-primary {
			display: flex;
			justify-content: space-between;

			@include mq($from: md) {
				display: flex;
				justify-content: space-between;
				flex-grow: 1;
			}
		}

		&-secondary {
			display: flex;
			align-items: center;
			justify-content: flex-start;

			@include mq($until: md) {
				margin-top: $gutter-md;
			}

			.btn {
				font-size: 24px;
			}
		}

		&-spacer {
			margin: 0 $gutter-xl;
			height: 24px;
			width: 2px;
			background: $neutral-3;

			@include mq($until: md) {
				display: none;
			}
		}
	}

	&__header-secondary {
		position: relative;
		justify-content: space-between;

		&-toggle + &-tooltip {
			display: none;
		}

		&-toggle:hover + &-tooltip {
			display: inline;
		}
	}

	::v-deep .btn--icon-only-circle {
		min-width: auto;
	}

	&__group {
		margin-bottom: $gutter-md;

		@include mq($from: md) {
			flex-wrap: wrap;
		}

		@include mq($from: md-l) {
			display: flex;
		}

		&-primary {
			@include mq($from: md) {
				display: inline-flex;
				width: 100%;
				gap: $gutter-xl;
			}

			@include mq($from: md-l) {
				width: 300px;
				display: initial;
				margin-right: $gutter-xl;
			}

			.impact-form__group-item {
				@include mq($from: md) {
					width: 100%;
				}
			}
		}

		&-secondary {
			@include mq($from: md) {
				width: 100%;
			}

			@include mq($from: md-l) {
				width: 400px;
			}
		}

		&-item {
			margin-bottom: $gutter-xl;
		}
	}
}

.impact-form__footer {
	padding: $padding-md 0;
	text-align: right;

	&-actions {
		display: inline-flex;
		align-items: initial;
	}
}

.impact-form__footer-message {
	font-size: $default-font-size - 2;
	color: $neutral-2;

	@include mq($from: sm-l) {
		margin-right: $gutter-xxl;
	}

	&-item {
		@include mq($until: sm-l) {
			display: flex;
			justify-content: flex-end;
			margin-bottom: $gutter-md;
		}
	}
}
</style>
